const TAG_TOOLS = new Set(['read_tags', 'query_tag_history', 'list_tags', 'get_tag_history_status'])
const ALARM_TOOLS = new Set(['get_active_alarms', 'get_alarm_status', 'get_opc_alarm_summary'])
const GRAPH_TOOLS = new Set(['run_ontology_query', 'get_nodes', 'resolve_plant_links'])

const CREATE_DASHBOARD_TOOLS = new Set(['add_dashboard'])
const MODIFY_DASHBOARD_TOOLS = new Set(['edit_dashboard', 'run_dashboard_script'])
const CREATE_AGENT_TOOLS = new Set(['create_custom_agent_config', 'create_agent_from_template'])
const MODIFY_AGENT_TOOLS = new Set(['update_custom_agent_config', 'delete_custom_agent_config'])
const CREATE_DOCUMENT_TOOLS = new Set(['add_or_edit_doc', 'add_chat_document'])
const MODIFY_DOCUMENT_TOOLS = new Set(['find_and_replace_doc', 'edit_chat_document'])
const CREATE_REPORT_TOOLS = new Set(['fill_template'])
const READ_ARTIFACT_TOOLS = new Set([
  'list_dashboards',
  'read_dashboard',
  'get_dashboard',
  'grep_dashboard',
  'capture_dashboard_screenshot',
  'list_custom_agent_configs',
  'get_custom_agent_config',
  'grep_custom_agent_config',
  'read_custom_agent_schema',
  'read_custom_agent_results',
  'run_custom_agent',
  'list_agent_templates',
  'view_agent_template',
  'list_ingest_artifacts',
  'get_ingest_artifact_info',
  'list_docs',
  'read_doc',
  'search_docs',
  'list_document_templates',
  'get_document_template'
])

const IGNORED_TOOLS = new Set([
  'switch_to_agent_mode',
  'finalize_answer',
  'finalize_plan_summary',
  'finalize_custom_agent_config_action',
  'finalize_dashboard_action',
  'finalize_document_action', // Historical Documents Agent transcripts.
  'finalize_ingest',
  'finalize_notification_rule_action',
  'read_user_guide',
  'get_current_time',
  'list_branches',
  'dispatch_subagent',
  'create_plan',
  'respond_in_text'
])

export type ArtifactWriteKind = 'created' | 'modified'

/** Write artifacts on the collapsed line, in display order. Nouns stay plural. */
const WRITE_NOUNS = [
  ['dashboards', 'dashboards'],
  ['agents', 'custom agents'],
  ['documents', 'documents'],
  ['reports', 'reports']
] as const

const MAX_SUMMARY_PARTS = 3

export function isIgnoredReasoningTool(name: string): boolean {
  return IGNORED_TOOLS.has(name)
}

export interface ReasoningToolCall {
  name: string
  input?: unknown
  output?: unknown
}

export interface ReasoningCounts {
  tags: number
  alarms: number
  graph: number
  other: number
  dashboards: ArtifactWriteKind | null
  agents: ArtifactWriteKind | null
  documents: ArtifactWriteKind | null
  reports: ArtifactWriteKind | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function stringIds(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

function countListedItems(value: unknown): number {
  if (Array.isArray(value)) return value.length
  const rec = asRecord(value)
  if (rec === null) return 0
  for (const key of ['alarms', 'active_alarms', 'items', 'results', 'values', 'tags', 'rows']) {
    if (Array.isArray(rec[key])) return rec[key].length
  }
  if (typeof rec.count === 'number' && Number.isFinite(rec.count) && rec.count >= 0) {
    return rec.count
  }
  if (typeof rec.active_count === 'number' && Number.isFinite(rec.active_count)) {
    return rec.active_count
  }
  return 0
}

function idsFromCall(call: ReasoningToolCall): string[] {
  const input = asRecord(call.input)
  if (input !== null) {
    const fromInput = stringIds(input.node_ids ?? input.nodeIds ?? input.tag_ids ?? input.tagIds)
    if (fromInput.length > 0) return fromInput
  }
  const fromOutput = countListedItems(call.output)
  if (fromOutput > 0) return Array.from({ length: fromOutput }, (_, i) => `${call.name}:${i}`)
  return [`${call.name}:call`]
}

function rememberWrite(
  current: ArtifactWriteKind | null,
  next: ArtifactWriteKind
): ArtifactWriteKind {
  // Created wins if the turn both added and edited — the line only has room
  // for one verb per artifact type.
  if (current === 'created' || next === 'created') return 'created'
  return 'modified'
}

export function countReasoningLookups(calls: readonly ReasoningToolCall[]): ReasoningCounts {
  const tagIds = new Set<string>()
  const alarmIds = new Set<string>()
  const graphIds = new Set<string>()
  const otherIds = new Set<string>()
  let dashboards: ArtifactWriteKind | null = null
  let agents: ArtifactWriteKind | null = null
  let documents: ArtifactWriteKind | null = null
  let reports: ArtifactWriteKind | null = null

  calls.forEach((call, callIndex) => {
    const name = call.name
    if (IGNORED_TOOLS.has(name) || READ_ARTIFACT_TOOLS.has(name)) return

    if (CREATE_DASHBOARD_TOOLS.has(name)) {
      dashboards = rememberWrite(dashboards, 'created')
      return
    }
    if (MODIFY_DASHBOARD_TOOLS.has(name)) {
      dashboards = rememberWrite(dashboards, 'modified')
      return
    }
    if (CREATE_AGENT_TOOLS.has(name)) {
      agents = rememberWrite(agents, 'created')
      return
    }
    if (MODIFY_AGENT_TOOLS.has(name)) {
      agents = rememberWrite(agents, 'modified')
      return
    }
    if (CREATE_DOCUMENT_TOOLS.has(name)) {
      documents = rememberWrite(documents, 'created')
      return
    }
    if (MODIFY_DOCUMENT_TOOLS.has(name)) {
      documents = rememberWrite(documents, 'modified')
      return
    }
    if (CREATE_REPORT_TOOLS.has(name)) {
      reports = rememberWrite(reports, 'created')
      return
    }

    if (TAG_TOOLS.has(name)) {
      for (const id of idsFromCall(call)) tagIds.add(id)
      return
    }
    if (ALARM_TOOLS.has(name)) {
      for (const id of idsFromCall(call)) alarmIds.add(`${callIndex}:${id}`)
      return
    }
    if (GRAPH_TOOLS.has(name)) {
      for (const id of idsFromCall(call)) graphIds.add(`${callIndex}:${id}`)
      return
    }
    for (const id of idsFromCall(call)) otherIds.add(`${callIndex}:${id}`)
  })

  return {
    tags: tagIds.size,
    alarms: alarmIds.size,
    graph: graphIds.size,
    other: otherIds.size,
    dashboards,
    agents,
    documents,
    reports
  }
}

export function elapsedSecondsBetween(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): number | null {
  if (!startedAt || !finishedAt) return null
  const start = Date.parse(startedAt)
  const end = Date.parse(finishedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null
  return Math.floor((end - start) / 1000)
}

export function formatElapsedSeconds(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return ''
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function capitalize(value: string): string {
  if (value.length === 0) return value
  return value[0].toUpperCase() + value.slice(1)
}

/**
 * Client-side past-tense line for a finished turn.
 * Writes (dashboards, custom agents, documents, reports) are "Created" or
 * "Modified" with no count. Live-data lookups stay quantified ("Checked 4 tags").
 * Graph-only turns say "Checked the graph".
 */
export function formatReasoningSummary(
  counts: ReasoningCounts,
  durationSeconds: number | null
): string {
  const writeParts: string[] = []
  for (const [key, noun] of WRITE_NOUNS) {
    const kind = counts[key]
    if (kind === 'created') writeParts.push(`created ${noun}`)
    else if (kind === 'modified') writeParts.push(`modified ${noun}`)
    if (writeParts.length >= MAX_SUMMARY_PARTS) break
  }

  const checkParts: string[] = []
  if (writeParts.length < MAX_SUMMARY_PARTS && counts.tags > 0) {
    checkParts.push(plural(counts.tags, 'tag'))
  }
  if (writeParts.length + checkParts.length < MAX_SUMMARY_PARTS && counts.alarms > 0) {
    checkParts.push(plural(counts.alarms, 'alarm'))
  }

  let body: string
  if (writeParts.length > 0 && checkParts.length > 0) {
    body = `${capitalize(writeParts.join(', '))}, checked ${checkParts.join(', ')}`
  } else if (writeParts.length > 0) {
    body = capitalize(writeParts.join(', '))
  } else if (checkParts.length > 0) {
    body = `Checked ${checkParts.join(', ')}`
  } else if (counts.graph > 0) {
    body = 'Checked the graph'
  } else if (counts.other > 0) {
    body = `Checked ${plural(counts.other, 'source')}`
  } else {
    body = 'Looked into this'
  }

  const duration =
    durationSeconds !== null && durationSeconds > 0 ? formatElapsedSeconds(durationSeconds) : ''
  return duration.length > 0 ? `${body} · ${duration}` : body
}
